'use client';

import {useEffect, useState} from 'react';

import {useI18n} from '@udemy-v2/i18n';

import {useTopicQuery} from '@/generated/graphql';

import type {EnrollmentStats, TrendingSkillsData, TrendingSkillsVariant} from '../types';
import {
    getCategoryTopicOverrides,
    getTrendingCategories,
    parseTrendingSkillsResponse,
} from '../parse';
import {monolithFetch} from './monolith-fetch';

interface SdTag {
    id: number;
    title: string;
    url: string;
    type: string;
}

interface CategoryStatsResponse {
    stats?: Array<{sd_tag: SdTag}>;
}

/** Fetch a fixed set of topics by ID via GraphQL — used for the India/Japan topic-ID overrides. */
async function fetchOverrideTopics(topicIds: number[]): Promise<CategoryStatsResponse> {
    const results = await Promise.all(
        topicIds.map(async (id) => {
            try {
                const {topic} = await useTopicQuery.fetcher({id: String(id)})();
                if (!topic) return null;
                return {
                    sd_tag: {id: Number(topic.id), title: topic.name, url: topic.url, type: 'topic'},
                };
            } catch {
                return null;
            }
        }),
    );
    return {stats: results.filter((r): r is NonNullable<typeof r> => r !== null)};
}

/**
 * Two-phase REST waterfall: category topics → enrollment stats → parse.
 * For the india/japan variants, category topics come from a fixed topic-ID override
 * (fetched via GraphQL) instead of the popular-topics REST call, mirroring v1's
 * `categoryToTopicMapForIndia`/`categoryToTopicMapForJapan`.
 */
export function useTrendingSkills(
    variant: TrendingSkillsVariant = 'default',
): TrendingSkillsData | undefined | null {
    const {gettext, locale} = useI18n();
    const [data, setData] = useState<TrendingSkillsData | undefined | null>(undefined);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                const categories = getTrendingCategories(variant);
                const overrides = getCategoryTopicOverrides(variant);

                const categoryParams = new URLSearchParams({
                    'fields[course_category]': 'stats',
                    stat_types: 'popular_topics_in_category',
                    stat_size: '4',
                    stat_role: 'marketplace',
                });
                const categoryResponses = await Promise.all(
                    categories.map((cat) => {
                        const overrideIds = overrides[cat.id];
                        if (overrideIds) {
                            return fetchOverrideTopics(overrideIds);
                        }
                        return monolithFetch<CategoryStatsResponse>(
                            `/api-2.0/structured-data/tags/category/${cat.id}/?${categoryParams}`,
                            locale,
                        );
                    }),
                );

                const categoryTopicIds = categoryResponses.flatMap(
                    (resp) => resp.stats?.map((s) => s.sd_tag.id) ?? [],
                );
                if (categoryTopicIds.length === 0) {
                    throw new Error('No topic IDs from categories');
                }

                const topSkill = {
                    id: 158002,
                    title: gettext('ChatGPT'),
                    url: '/topic/chatgpt/',
                    type: 'topic',
                };
                const allIds = [topSkill.id, ...categoryTopicIds];

                const enrollmentResp = await monolithFetch<EnrollmentStats>(
                    `/api-2.0/structured-data/enrollment-stats/?topic_ids=${allIds.join(',')}`,
                    locale,
                );
                if (cancelled) return;

                const parsed = parseTrendingSkillsResponse(
                    categoryResponses,
                    enrollmentResp,
                    topSkill,
                    categories,
                );

                const hasTopics = parsed.categoryTopics.some((c) => c.topics.length > 0);
                setData(hasTopics ? parsed : null);
            } catch {
                if (!cancelled) setData(null);
            }
        };

        fetchData();
        return () => {
            cancelled = true;
        };
    }, [gettext, locale, variant]);

    return data;
}
